import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, CssBaseline, Box, CircularProgress } from '@mui/material';
import '@fontsource/poppins'; 

import theme from './theme'; 
import { auth, db } from './firebase';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { doc, onSnapshot } from 'firebase/firestore';

import Login from './components/Login';
import Layout from './components/Layout';
import Home from './components/Home';
import Dashboard from './components/Dashboard'; 
import BancoDetalhes from './components/BancoDetalhes'; 
import GastosMensais from './components/GastosMensais'; 
import Investimentos from './components/Investimentos'; 
import Emprestimos from './components/Emprestimos';
import CaixinhaDetalhes from './components/CaixinhaDetalhes'; 
import Calculadora from './components/Calculadora'; 
import LancamentoRapido from './components/LancamentoRapido';
import Admin from './components/Admin';
import PrimeiroAcesso from './components/PrimeiroAcesso';
import AccessBlocked from './components/AccessBlocked';

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let userUnsub = null;
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      if (userUnsub) {
        userUnsub();
        userUnsub = null;
      }

      if (currentUser) {
        userUnsub = onSnapshot(
          doc(db, 'users', currentUser.uid),
          (userDoc) => {
            if (userDoc.exists()) {
              const userData = userDoc.data();
              const expiraEm = userData.expiraEm ? new Date(userData.expiraEm) : new Date(2000, 0, 1);
              const hoje = new Date();

              const isBlocked = (userData.status === 'bloqueado' || expiraEm < hoje) && userData.role !== 'admin';
              const mergedUser = Object.assign(currentUser, userData, { blocked: isBlocked });
              setUser(mergedUser);
            } else {
              setUser(currentUser);
            }
            setLoading(false);
          },
          () => {
            setUser(currentUser);
            setLoading(false);
          }
        );
      } else {
        setUser(null);
        setLoading(false);
      }
    });
    return () => {
      if (userUnsub) userUnsub();
      unsubscribe();
    };
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress sx={{ color: '#1a237e' }} />
      </Box>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        {!user ? (
          <Login />
        ) : user?.precisaDefinirSenha ? (
          <PrimeiroAcesso
            user={user}
            onConcluir={(patch) =>
              setUser((prev) => {
                if (!prev) return patch || null;
                // Cria um novo objeto preservando o protótipo do Firebase User
                const clone = Object.create(Object.getPrototypeOf(prev));
                return Object.assign(clone, prev, patch || {});
              })
            }
          />
        ) : user?.blocked ? (
          <AccessBlocked user={user} />
        ) : (
          <Layout user={user}>
            <Routes>
              <Route path="/" element={<Home user={user} />} />
              <Route path="/bancos" element={<Dashboard user={user} />} />
              <Route path="/banco/:bancoId" element={<BancoDetalhes user={user} />} />
              <Route path="/gastos" element={<GastosMensais user={user} />} />
              <Route path="/investimentos" element={<Investimentos user={user} />} />
              <Route path="/investimentos/:id" element={<CaixinhaDetalhes user={user} />} />
              <Route path="/emprestimos" element={<Emprestimos user={user} />} />
              <Route path="/calculadora" element={<Calculadora />} />
              <Route path="/lancamento" element={<LancamentoRapido user={user} />} />
              <Route path="/admin" element={<Admin user={user} />} />
              <Route path="*" element={<Navigate to="/" />} />
            </Routes>
          </Layout>
        )}
      </Router>
    </ThemeProvider>
  );
}

export default App;
